import "./App.css";
import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import "./index.css";
import React, { useEffect, useState, useCallback, useRef } from "react";
import CompetitionBrand from "./components/CompetitionBrand";
import { Helmet } from "react-helmet-async";
import { getCurrentUser } from "aws-amplify/auth";
import {
  directorModeLabel,
  directorModePath,
  loadCompetitionDirectorSignal,
} from "./lib/competitionCapabilities";
import { isCompetitionTestAuthEnabled } from "./lib/leaderboardApi";
import {
  buildAuthReturnPath,
  buildCurrentReturnTo,
  getReturnToFromLocation,
  isCompetitionReturnTo,
} from "./lib/authReturn";

import { SITE_URL, PUBLIC_ROBOTS } from "./utils/siteSeo.mjs";
const DEFAULT_SOCIAL_IMAGE = `${SITE_URL}/features/feature-armory-status.webp`;

const seoLandingMeta = {
  "/firearm-inventory-app": {
    title: "Firearm Inventory App | Collection Records & Range History",
    description:
      "Range Pocket helps responsible firearm owners track firearms, ammo, accessories, receipts, photos, maintenance, and range history in one organized app.",
    image: `${SITE_URL}/firearm_list.png`,
  },
  "/range-log-app": {
    title: "Range Log App | Track Range Visits, Notes & Round Counts",
    description:
      "Range Pocket helps shooters log range visits, firearms used, round counts, target photos, notes, range locations, training plans, and performance history.",
    image: `${SITE_URL}/range_log.png`,
  },
  "/firearm-maintenance-tracker": {
    title: "Firearm Maintenance Tracker | Logs, Round Counts & Reminders",
    description:
      "Range Pocket helps shooters track firearm maintenance, round counts, accessory changes, battery reminders, notes, photos, and range history in one organized app.",
    image: `${SITE_URL}/content/blog/firearm-maintenance-log-what-to-track/maintenance_plan_screen.png`,
  },
    "/features": {
      title: "Range Pocket Features | Firearm Inventory, Range Logs & Training",
      description:
        "Explore Range Pocket features for firearm inventory, range visit logging, ammo tracking, maintenance, training plans, accessories, saved ranges, photos, and notes.",
      image: `${SITE_URL}/features/feature-armory-status.webp`,
  },
  "/features/firearm-inventory": {
    title: "Firearm Inventory Tracking Feature | Range Pocket",
    description:
      "Track firearm photos, purchase details, accessories, configurations, maintenance notes, round counts, and range usage history with Range Pocket.",
    image: `${SITE_URL}/features/feature-firearm-list.webp`,
  },
  "/features/range-visit-logging": {
    title: "Range Visit Logging Feature for Shooters | Range Pocket",
    description:
      "Use Range Pocket as a range log app for firearms used, round counts, target photos, notes, saved ranges, drills, conditions, and training context.",
    image: `${SITE_URL}/features/feature-range-visits.webp`,
  },
  "/features/ammo-tracking": {
    title: "Ammo Tracking Feature for Firearm Owners | Range Pocket",
    description:
      "Keep ammunition records, usage context, firearm compatibility, range visit history, and inventory notes connected in Range Pocket.",
    image: `${SITE_URL}/features/feature-inventory-dashboard.webp`,
  },
  "/features/firearm-maintenance": {
    title: "Firearm Maintenance Tracking Feature | Range Pocket",
    description:
      "Track firearm maintenance notes, cleaning history, round counts, accessory changes, battery reminders, photos, and range use in Range Pocket.",
    image: `${SITE_URL}/content/blog/firearm-maintenance-log-what-to-track/maintenance_plan_screen.png`,
  },
  "/features/training-plans": {
    title: "Shooting Training Plan Feature | Range Pocket",
    description:
      "Create shooting training plans, custom drills, range visit checklists, and progress history that connects back to your Range Pocket logs.",
    image: `${SITE_URL}/features/feature-training-overview.webp`,
  },
  "/features/accessory-tracking": {
    title: "Firearm Accessory Tracking Feature | Range Pocket",
    description:
      "Track firearm accessories, optics, parts, battery reminders, custom builds, linked equipment, and configuration records with Range Pocket.",
    image: `${SITE_URL}/features/feature-gunsmith-profile.webp`,
  },
  "/partners": {
    title: "Range Pocket Partner Program | ArmorySync",
    description:
      "Apply for the Range Pocket Partner Program and share one clean Range Pocket download link with firearm owners, students, members, and shooting communities.",
    image: `${SITE_URL}/features/feature-armory-status.webp`,
  },
};

const companyNavItems = [
  {
    to: "/products",
    label: "Products",
    activePrefixes: [
      "/products",
      "/range-pocket",
      "/features",
      "/firearm-inventory-app",
      "/range-log-app",
      "/firearm-maintenance-tracker",
    ],
  },
  { to: "/competition", label: "Competition" },
  { to: "/partners", label: "Partners", activePrefixes: ["/partners", "/r/"] },
  { to: "/blog", label: "Blog" },
  { to: "/about", label: "About" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

const publicCompetitionNavItems = [
  { to: "/matches", label: "Find matches", end: true, matchView: "upcoming" },
  { to: "/matches?status=completed", label: "Past matches", end: true, matchView: "past" },
  { to: "/tournaments", label: "Tournaments" },
];

const signedInCompetitionNavItems = [
  { to: "/matches", label: "Find matches", end: true, matchView: "upcoming" },
  { to: "/matches?status=completed", label: "Past matches", end: true, matchView: "past" },
  { to: "/tournaments", label: "Tournaments" },
  { to: "/matches/my", label: "My Matches" },
  { to: "/matches/updates", label: "Updates" },
];

const directorNavItems = [
  {
    to: "/matches/manage/matches",
    label: "Matches",
    activePrefixes: [
      "/matches/manage/matches",
      "/matches/manage/new",
      "/matches/manage/arc",
      "/matches/manage/bullseye",
      "/matches/manage/uspsa",
      "/leaderboards/manage/matches",
      "/leaderboards/manage/arc",
      "/leaderboards/manage/bullseye",
      "/leaderboards/manage/uspsa"
    ]
  },
  { to: "/matches/manage/tournaments", label: "Tournaments" },
  { to: "/matches/manage/displays", label: "Displays" },
];

function isDisplayRoute(pathname) {
  return (
    pathname.startsWith("/matches/show") ||
    pathname.startsWith("/leaderboards/show") ||
    /^\/matches\/[^/]+\/[^/]+\/display$/.test(pathname) ||
    /^\/leaderboards\/matches\/[^/]+\/display$/.test(pathname) ||
    /^\/tournaments\/[^/]+\/leaderboard$/.test(pathname) ||
    /^\/leaderboards\/tournaments\/[^/]+$/.test(pathname)
  );
}

function isCompetitionRoute(pathname) {
  return (
    pathname.startsWith("/competition") ||
    pathname.startsWith("/matches") ||
    pathname.startsWith("/tournaments") ||
    pathname.startsWith("/leaderboards")
  );
}

function isDirectorRoute(pathname) {
  return pathname.startsWith("/matches/manage") || pathname.startsWith("/leaderboards/manage");
}

function isPrivateCompetitionRoute(pathname) {
  return (
    pathname.startsWith("/matches/my") ||
    pathname.startsWith("/matches/manage") ||
    pathname.startsWith("/leaderboards/manage") ||
    pathname.startsWith("/competition/account")
  );
}

function isCompetitionAuthRequest(search) {
  const returnTo = getReturnToFromLocation({ search }, "");
  return Boolean(returnTo) && isCompetitionReturnTo(returnTo);
}

function getOwnedMatchFamilyTitle(search) {
  const family = new URLSearchParams(search).get("family");
  const labelByFamily = {
    arc: "NRA ARC",
    nra_arc: "NRA ARC",
    bullseye: "Bullseye",
    uspsa: "USPSA"
  };
  return labelByFamily[family] || "";
}

export function getDocumentTitle(pathname, search = "") {
  if (pathname === "/home" || pathname === "/") {
    return "Range Pocket by ArmorySync | Firearm Inventory & Range Log App";
  }
  if (pathname === "/products") {
    return "ArmorySync Products | Range Pocket and RangeGrid";
  }
  if (pathname === "/range-pocket" || pathname === "/RangePocket") {
    return "Range Pocket | Firearm Inventory, Range Log & Training App";
  }
  if (pathname === "/about") {
    return "About ArmorySync | Company Behind Range Pocket";
  }
  if (pathname === "/faq") {
    return "Range Pocket FAQ | Records, Features & Platforms";
  }
  if (pathname === "/contact") {
    return "Contact ArmorySync | Product Support";
  }
  if (pathname === "/blog") {
    return "ArmorySync Blog | Firearm App Updates & Training Insights";
  }
  if (seoLandingMeta[pathname]) {
    return seoLandingMeta[pathname].title;
  }
  if (pathname.startsWith("/matches/show/nra") || pathname.startsWith("/leaderboards/show/nra")) {
    return "NRA Show Playlist | Range Pocket Competition";
  }
  if (isDisplayRoute(pathname)) {
    return "Fullscreen Leaderboard | Range Pocket Competition";
  }
  if (pathname === "/auth/sign-in") {
    return isCompetitionAuthRequest(search) ? "Sign In to Range Pocket | Range Pocket Competition" : "Sign In | ArmorySync";
  }
  if (pathname === "/auth/sign-up") {
    return isCompetitionAuthRequest(search) ? "Create Range Pocket Account | Range Pocket Competition" : "Create Account | ArmorySync";
  }
  if (pathname === "/auth/forgot-password") {
    return isCompetitionAuthRequest(search) ? "Reset Password | Range Pocket Competition" : "Reset Password | ArmorySync";
  }
  if (pathname === "/matches/manage/tournaments/new") {
    return "New Tournament | Range Pocket Competition";
  }
  if (/^\/matches\/manage\/tournaments\/[^/]+\/settings$/.test(pathname)) {
    return "Tournament Settings | Range Pocket Competition";
  }
  if (/^\/matches\/manage\/tournaments\/[^/]+/.test(pathname)) {
    return "Tournament Workbench | Range Pocket Competition";
  }
  if (pathname.startsWith("/matches/manage/tournaments")) {
    return "Tournament Admin | Range Pocket Competition";
  }
  if (pathname.startsWith("/matches/manage/displays")) {
    return "Displays | Range Pocket Competition";
  }
  if (/^\/matches\/manage\/(arc|bullseye)\/[^/]+\/audit\/?$/.test(pathname)) {
    return "Action Audit | Range Pocket Competition";
  }
  if (/^\/matches\/manage\/(arc|bullseye|uspsa)\/[^/]+/.test(pathname)) {
    return "Registration Workbench | Range Pocket Competition";
  }
  if (pathname === "/matches/manage/bullseye" || pathname === "/matches/manage/uspsa" || pathname === "/matches/manage/arc") {
    return "Owned Matches | Range Pocket Competition";
  }
  if (pathname.startsWith("/matches/manage/new")) {
    return "Create Match | Range Pocket Competition";
  }
  if (pathname === "/matches/manage/matches") {
    const familyTitle = getOwnedMatchFamilyTitle(search);
    return familyTitle
      ? `Owned Matches - ${familyTitle} | Range Pocket Competition`
      : "Owned Matches | Range Pocket Competition";
  }
  if (pathname.startsWith("/matches/manage")) {
    return "Director Console | Range Pocket Competition";
  }
  if (pathname === "/matches" || pathname === "/matches/") {
    return "Matches | Range Pocket Competition";
  }
  if (pathname === "/matches/bullseye" || pathname === "/matches/bullseye/") {
    return "Bullseye Matches | Range Pocket Competition";
  }
  if (pathname === "/matches/updates") return "Your updates | Range Pocket";
  if (/^\/matches\/(arc|bullseye)\/[^/]+\/updates$/.test(pathname)) return "Match updates | Range Pocket";
  if (pathname.startsWith("/matches/my")) {
    return "My Matches | Range Pocket Competition";
  }
  if (pathname.startsWith("/matches/results")) {
    return "Competitor Score Detail | Range Pocket Competition";
  }
  if (/^\/matches\/(arc|bullseye|uspsa)\//.test(pathname)) {
    return "Match Detail | Range Pocket Competition";
  }
  if (pathname === "/tournaments" || pathname === "/tournaments/") {
    return "Tournaments | Range Pocket Competition";
  }
  if (pathname.startsWith("/tournaments")) {
    return "Tournament | Range Pocket Competition";
  }
  if (pathname.startsWith("/competition")) {
    if (pathname.startsWith("/competition/account")) {
      return "Competition Account | Range Pocket Competition";
    }
    return "Range Pocket Competition | ArmorySync";
  }
  return "ArmorySync - Practical Firearm Management Software";
}

function getMetaDescription(pathname, search = "") {
  if (pathname === "/home" || pathname === "/") {
    return "Download Range Pocket, a firearm inventory and range log app for responsible owners to track firearms, ammo, maintenance, range visits, and training.";
  }
  if (pathname === "/products") {
    return "Explore ArmorySync products: Range Pocket for shooters and firearm owners, and RangeGrid for range owners and operators.";
  }
  if (pathname === "/range-pocket" || pathname === "/RangePocket") {
    return "Range Pocket helps shooters track firearms, ammo, accessories, range visits, maintenance, training plans, and performance in one organized app.";
  }
  if (pathname === "/about") {
    return "Learn about ArmorySync, the company behind Range Pocket, and how we build practical firearm record and range workflow tools.";
  }
  if (pathname === "/faq") {
    return "Answers about Range Pocket records, firearm inventory tracking, training tools, supported platforms, subscriptions, and support.";
  }
  if (pathname === "/contact") {
    return "Contact ArmorySync for Range Pocket support, product questions, setup feedback, and owner workflow ideas.";
  }
  if (pathname === "/blog") {
    return "Read ArmorySync updates about Range Pocket, firearm inventory, range visit tracking, maintenance, training workflows, and training habits for responsible shooters.";
  }
  if (seoLandingMeta[pathname]) {
    return seoLandingMeta[pathname].description;
  }
  if (isDisplayRoute(pathname)) {
    return "Fullscreen Range Pocket Competition leaderboards for range monitors, match staff, and tournament standings.";
  }
  if (pathname.startsWith("/auth/")) {
    return isCompetitionAuthRequest(search)
      ? "Sign in to Range Pocket Competition to manage match registrations, competitor profile details, and director workflows."
      : "Sign in to ArmorySync to manage your account and product access.";
  }
  if (pathname.startsWith("/matches/manage") || pathname.startsWith("/leaderboards/manage")) {
    if (pathname.startsWith("/matches/manage/displays")) {
      return "Director display launcher for owned match and tournament fullscreen leaderboards.";
    }
    return "Director workspace for creating Range Pocket matches, managing rosters, reviewing setup, and opening fullscreen leaderboards.";
  }
  if (pathname.startsWith("/matches/my")) {
    return "Signed-in Range Pocket competitor workspace for registrations, upcoming matches, competition IDs, and results.";
  }
  if (pathname.startsWith("/competition/account")) {
    return "Manage Range Pocket competition account details and IDs for match registration workflows.";
  }
  if (pathname.startsWith("/matches/results")) {
    return "Review a Range Pocket competitor score detail from match results.";
  }
  if (/^\/matches\/(arc|bullseye|uspsa)\//.test(pathname)) {
    return "Review Range Pocket match details, registration status, results, and fullscreen leaderboard views.";
  }
  if (pathname === "/matches/bullseye" || pathname === "/matches/bullseye/") {
    return "Browse public Bullseye matches in Range Pocket Competition.";
  }
  if (pathname === "/matches" || pathname.startsWith("/matches/")) {
    return "Browse Range Pocket public matches, review match details, and register when registration is available.";
  }
  if (pathname.startsWith("/tournaments")) {
    return "Browse Range Pocket public tournaments, leaderboards, included matches, and standings.";
  }
  if (pathname.startsWith("/competition")) {
    return "Range Pocket Competition public doorway for matches, tournaments, registration, and standings.";
  }
  if (pathname.startsWith("/leaderboards")) {
    return "View Range Pocket competition leaderboards, results, and fullscreen scoring views.";
  }
  return "ArmorySync builds Range Pocket, a firearm inventory and range log app for managing firearms, accessories, maintenance, range visits, photos, and notes.";
}

function getStructuredData(pathname, canonicalUrl) {
  const organization = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${SITE_URL}/#organization`,
    name: "ArmorySync",
    url: SITE_URL,
    logo: `${SITE_URL}/apple-touch-icon.png`,
    sameAs: ["https://www.instagram.com/armorysync/"],
    contactPoint: [
      {
        "@type": "ContactPoint",
        email: "support@armorysync.com",
        contactType: "customer support",
        availableLanguage: ["en"],
      },
    ],
  };
  const webSite = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    name: "ArmorySync",
    url: SITE_URL,
    publisher: { "@id": `${SITE_URL}/#organization` },
  };
  const product = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "@id": `${SITE_URL}/range-pocket#software`,
    name: "Range Pocket",
    url: `${SITE_URL}/range-pocket`,
    image: `${SITE_URL}/features/feature-armory-status.webp`,
    applicationCategory: "UtilityApplication",
    operatingSystem: "iOS, Android",
    publisher: { "@id": `${SITE_URL}/#organization` },
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
      availability: "https://schema.org/InStock",
    },
    downloadUrl: [
      "https://apps.apple.com/us/app/range-pocket/id6733222657",
      "https://play.google.com/store/apps/details?id=com.armorysync.rangepocket",
    ],
    description:
      "Range Pocket helps shooters track firearm inventory, range visits, ammo, accessories, maintenance, and training in one organized app.",
  };
  const breadcrumbNames = {
    "/home": "Home",
    "/products": "Products",
    "/range-pocket": "Range Pocket",
    "/competition": "Competition",
    "/blog": "Blog",
    "/about": "About",
    "/faq": "FAQ",
    "/contact": "Contact",
    "/firearm-inventory-app": "Firearm Inventory App",
    "/range-log-app": "Range Log App",
    "/firearm-maintenance-tracker": "Firearm Maintenance Tracker",
  };
  const data = [organization, webSite];

  if (pathname === "/range-pocket") {
    data.push(product);
  }

  if (breadcrumbNames[pathname]) {
    data.push({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "ArmorySync",
          item: `${SITE_URL}/home`,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: breadcrumbNames[pathname],
          item: canonicalUrl,
        },
      ],
    });
  }

  return data;
}

function ScrollToRoutePosition({ disabled = false }) {
  const location = useLocation();
  const previousPath = useRef(null);

  useEffect(() => {
    const filteringMatch = previousPath.current === location.pathname
      && (location.pathname === "/matches/my" || /^\/matches\/(arc|bullseye)\/[^/]+$/.test(location.pathname)) && !location.hash;
    previousPath.current = location.pathname;
    if (disabled || filteringMatch || typeof window === "undefined") {
      return undefined;
    }

    let frameId;
    let timeoutId;

    const scrollToTop = () => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };

    const scrollToHash = () => {
      const targetId = decodeURIComponent(location.hash.slice(1));
      const target = document.getElementById(targetId);

      if (target) {
        target.scrollIntoView({ block: "start", inline: "nearest", behavior: "auto" });
        return true;
      }

      return false;
    };

    frameId = window.requestAnimationFrame(() => {
      if (!location.hash) {
        scrollToTop();
        return;
      }

      if (!scrollToHash()) {
        timeoutId = window.setTimeout(() => {
          if (!scrollToHash()) {
            scrollToTop();
          }
        }, 80);
      }
    });

    return () => {
      window.cancelAnimationFrame(frameId);
      window.clearTimeout(timeoutId);
    };
  }, [disabled, location.key, location.pathname, location.search, location.hash]);

  return null;
}

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null), menuTriggerRef = useRef(null);
  useEffect(() => {
    if (!menuOpen) return undefined;
    const panel = menuRef.current, trigger = menuTriggerRef.current;
    panel?.querySelector("button")?.focus();
    const trap = event => {
      if (event.key !== "Tab") return;
      const controls = [...panel.querySelectorAll("a[href], button:not(:disabled)")];
      const first = controls[0], last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    panel?.addEventListener("keydown", trap);
    return () => { panel?.removeEventListener("keydown", trap); trigger?.focus(); };
  }, [menuOpen]);
  const [signedIn, setSignedIn] = useState(false);
  const [directorSignal, setDirectorSignal] = useState("unknown");
  const isShowRoute = isDisplayRoute(location.pathname);
  const competitionAuthShell = !isShowRoute && location.pathname.startsWith("/auth/") && isCompetitionAuthRequest(location.search);
  const competitionShell = !isShowRoute && (isCompetitionRoute(location.pathname) || competitionAuthShell);
  const directorShell = !isShowRoute && isDirectorRoute(location.pathname);
  const discoveryShell = /^\/matches\/?$/.test(location.pathname);
  const competitionHomeShell = /^\/competition\/?$/.test(location.pathname);
  const competitionSignInReturn = competitionAuthShell
    ? getReturnToFromLocation(location)
    : buildCurrentReturnTo(location);
  const navItems = competitionShell
    ? directorShell
      ? signedIn
        ? directorNavItems
        : []
      : competitionHomeShell
        ? [{ to: "/competition", label: "Home", end: true },
          publicCompetitionNavItems[0],
          { to: "/matches/my", label: "My Matches" },
          ...publicCompetitionNavItems.slice(1),
          ...(signedIn ? [{ to: "/matches/updates", label: "Updates" }] : [])]
      : signedIn
        ? signedInCompetitionNavItems
        : publicCompetitionNavItems
    : companyNavItems;
  const accountLink = signedIn
    ? { to: competitionShell ? "/competition/account" : "/profile", label: "Account" }
    : {
      to: competitionShell
        ? buildAuthReturnPath("/auth/sign-in", competitionSignInReturn)
        : "/auth/sign-in",
      label: "Sign In"
    };
  const isMatchNavActive = (item) => discoveryShell
    && (item.matchView === "past") === (new URLSearchParams(location.search).get("status") === "completed");
  const navLinkClassName = (item) => ({ isActive }) => {
    const prefixActive = Array.isArray(item.activePrefixes)
      ? item.activePrefixes.some((prefix) => location.pathname.startsWith(prefix)) || (item.to === "/matches/manage/matches" && location.pathname === "/matches/manage")
      : false;
    const matchViewActive = item.matchView && isMatchNavActive(item);
    return `cta-link${item.matchView ? (matchViewActive ? " is-active" : "") : isActive || prefixActive ? " is-active" : ""}`;
  };

  useEffect(() => {
    if (location.pathname === "/") {
      navigate("/home", { replace: true });
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    document.documentElement.classList.toggle("match-discovery-open", discoveryShell);
    return () => document.documentElement.classList.remove("match-discovery-open");
  }, [discoveryShell]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setMenuOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    let active = true;

    async function loadAuthState() {
      if (competitionShell && isCompetitionTestAuthEnabled()) {
        if (active) {
          setSignedIn(true);
        }
        return;
      }

      try {
        await getCurrentUser();
        if (active) {
          setSignedIn(true);
        }
      } catch {
        if (active) {
          setSignedIn(false);
          setDirectorSignal("unknown");
        }
      }
    }

    loadAuthState();
    return () => {
      active = false;
    };
  }, [competitionShell, location.pathname]);

  useEffect(() => {
    let active = true;

    async function loadDirectorSignal() {
      if (!signedIn || isShowRoute) {
        if (active && !signedIn) {
          setDirectorSignal("unknown");
        }
        return;
      }

      try {
        const nextSignal = await loadCompetitionDirectorSignal();
        if (active) {
          setDirectorSignal(nextSignal);
        }
      } catch {
        if (active) {
          setDirectorSignal("none");
        }
      }
    }

    loadDirectorSignal();
    return () => {
      active = false;
    };
  }, [isShowRoute, location.pathname, signedIn]);

  useEffect(() => {
    document.body.style.overflow = menuOpen && !isShowRoute ? "hidden" : "";
  }, [menuOpen, isShowRoute]);

  const closeMenu = useCallback(() => setMenuOpen(false), []);
  const normalizedPath =
    location.pathname !== "/" && location.pathname.endsWith("/")
      ? location.pathname.slice(0, -1)
      : location.pathname;
  const routePath = normalizedPath === "/RangePocket" ? "/range-pocket" : normalizedPath;
  const documentTitle = getDocumentTitle(routePath, location.search);
  const metaDescription = getMetaDescription(routePath, location.search);
  const canonicalPath = routePath === "/" ? "/home" : routePath;
  const canonicalUrl = `${SITE_URL}${canonicalPath}`;
  const socialImage = seoLandingMeta[canonicalPath]?.image || DEFAULT_SOCIAL_IMAGE;
  const siteName = isShowRoute || competitionShell ? "Range Pocket Competition" : "ArmorySync";
  const robotsContent = isShowRoute || location.pathname.startsWith("/auth/") || location.pathname.startsWith("/partners/dashboard") || location.pathname.startsWith("/partners/admin") || isPrivateCompetitionRoute(location.pathname) ? "noindex,nofollow" : PUBLIC_ROBOTS;
  const structuredData = getStructuredData(canonicalPath, canonicalUrl);
  const competitionModeLabel = competitionHomeShell ? "Director Console" : directorModeLabel(directorSignal);
  const competitionModePath = competitionHomeShell ? "/matches/manage" : directorModePath(directorSignal);
  const showCompetitionModeLink = competitionHomeShell;
  const competitorModePath = "/matches/my";

  useEffect(() => {
    document.title = documentTitle;
  }, [documentTitle]);

  return (
    <div className={`landing ${isShowRoute ? "landing-show" : ""} ${competitionShell ? "landing-competition" : ""} ${directorShell ? "landing-director" : ""} ${discoveryShell ? "landing-discovery" : ""}`}>
      <ScrollToRoutePosition disabled={isShowRoute} />
      <Helmet>
        <title>{documentTitle}</title>
        <meta
          name="description"
          content={metaDescription}
        />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:site_name" content={siteName} />
        <meta property="og:title" content={documentTitle} />
        <meta
          property="og:description"
          content={metaDescription}
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={socialImage} />
        <meta property="og:image:alt" content="ArmorySync Range Pocket firearm management app preview" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={documentTitle} />
        <meta
          name="twitter:description"
          content={metaDescription}
        />
        <meta name="twitter:image" content={socialImage} />
        <meta name="theme-color" content="#0c0e12" />
        <meta name="robots" content={robotsContent} />
        {structuredData.map((entry) => (
          <script key={entry["@id"] || entry["@type"]} type="application/ld+json">
            {JSON.stringify(entry)}
          </script>
        ))}
      </Helmet>

      {!isShowRoute && (
        <div className="bg-blur">
          <div className="blob b1" />
          <div className="blob b2" />
          <div className="blob b3" />
        </div>
      )}

      {!isShowRoute && (
        <header className={`nav ${competitionShell ? "nav-competition" : ""} ${competitionShell && signedIn ? "nav-competition-authenticated" : ""} ${directorShell ? "nav-director" : ""} ${directorShell && signedIn ? "nav-director-compact" : ""}`}>
          <Link to={competitionShell ? "/competition" : "/home"} className="brand" aria-label={competitionShell ? "Competition home" : "ArmorySync Home"}>
            {competitionShell ? (
              <CompetitionBrand director={directorShell} />
            ) : (
              <><img src="/armorysync_logo.png" className="logo-icon" alt="" /><span>ArmorySync</span></>
            )}
          </Link>

          <nav className="nav-links" aria-label="Primary">
            {navItems.map((item) => (
              <NavLink key={`${item.to}-${item.label}`} className={navLinkClassName(item)} to={item.to} end={item.end} aria-current={item.matchView ? (isMatchNavActive(item) ? "page" : false) : "page"}>
                {item.label}
              </NavLink>
            ))}
            {directorShell && signedIn && (
              <NavLink className="cta-link nav-mode-link" to={competitorModePath} end>
                Competitor View
              </NavLink>
            )}
            {showCompetitionModeLink && (
              <NavLink className="cta-link nav-mode-link" to={competitionModePath} end>
                {competitionModeLabel}
              </NavLink>
            )}
            <NavLink className={({ isActive }) => `cta-link nav-account-link${isActive ? " is-active" : ""}`} to={accountLink.to}>
              {accountLink.label}
            </NavLink>
          </nav>

          <button
            className={`hamburger ${menuOpen ? "is-open" : ""}`}
            ref={menuTriggerRef}
            aria-label={menuOpen ? "Close menu" : "Menu"}
            aria-expanded={menuOpen}
            aria-controls="mobile-menu"
            onClick={() => setMenuOpen((open) => !open)}
          >
            <span />
            <span />
            <span />
          </button>
        </header>
      )}

      {!isShowRoute && menuOpen && (
        <>
          <div className="mobile-menu-backdrop" onClick={closeMenu} aria-hidden="true" />
          <nav ref={menuRef} id="mobile-menu" className="mobile-menu" aria-label="Mobile" role="dialog" aria-modal="true">
            <button className="mobile-menu-close" type="button" onClick={closeMenu}>Close menu <span aria-hidden="true">×</span></button>
            {navItems.map((item) => (
              <NavLink key={`${item.to}-${item.label}`} className={navLinkClassName(item)} to={item.to} end={item.end} onClick={closeMenu} aria-current={item.matchView ? (isMatchNavActive(item) ? "page" : false) : "page"}>
                {item.label}
              </NavLink>
            ))}
            {directorShell && signedIn && (
              <NavLink to={competitorModePath} onClick={closeMenu} end>
                Competitor View
              </NavLink>
            )}
            {showCompetitionModeLink && (
              <NavLink to={competitionModePath} onClick={closeMenu} end>
                {competitionModeLabel}
              </NavLink>
            )}
            <NavLink to={accountLink.to} onClick={closeMenu}>
              {accountLink.label}
            </NavLink>
          </nav>
        </>
      )}

      <main className={`app-main ${isShowRoute ? "app-main-show" : ""} ${directorShell ? "app-main-director" : ""}`}>
        <Outlet />
      </main>

      {!isShowRoute && !discoveryShell && (
        <footer className="nav footer-nav">
          <p>
            {competitionShell
              ? (
                <>
                  APEX by RangePocket. Copyright {new Date().getFullYear()} ArmorySync LLC.{" "}
                  <Link to="/home" className="footer-company-link">Company Site</Link>
                </>
              )
              : `Copyright ${new Date().getFullYear()} ArmorySync LLC. All rights reserved.`}
          </p>
        </footer>
      )}
    </div>
  );
}

export default App;
